/**
 * Campo de archivo
 * -----------------------------------------------------------------------------
 * El `<input type="file">` nativo se ve distinto en cada navegador, no se puede
 * estilar y su texto («Sin archivos seleccionados») no se traduce. Aquí queda
 * escondido y se pinta encima un control propio, que además enseña el nombre y
 * el tamaño de lo elegido y deja quitarlo.
 *
 *   <div class="company-file" data-company-file-init
 *        data-company-file-accept="application/pdf"
 *        data-company-file-max="5">
 *     <input type="file" id="rut" name="rut" />
 *     <label class="form-label" for="rut">RUT en PDF</label>
 *   </div>
 *
 * El límite de tamaño es en MEGABYTES y se comprueba aquí solo para avisar
 * pronto: quien decide de verdad es el servidor. Un límite de cliente se salta
 * cambiando el HTML, así que `upload_max_filesize` sigue mandando.
 *
 * ## Imágenes a WebP antes de subirlas
 *
 * Con `data-company-file-webp`, una imagen elegida se convierte **aquí** y lo
 * que viaja son unos cientos de kilobytes en vez de veinticinco megas. Es la
 * misma conversión que hace la galería de fotos, y por los mismos dos motivos,
 * que en un alojamiento compartido no se arreglan con más código del lado del
 * servidor: GD descomprime la imagen entera en memoria —una foto de móvil son
 * doscientos megas solo para abrirla— y quedarse sin memoria en PHP no se puede
 * atrapar, mata la petición sin mensaje; y `post_max_size` rechaza el envío
 * entero sin poder decir por qué.
 *
 * Lo que no sea una imagen viaja intacto, y si el navegador no sabe escribir
 * WebP —o la imagen no se deja leer— se manda el original y lo convierte el
 * servidor. Es una mejora del camino normal, no un requisito.
 */

import { aWebp } from './a-webp.js';
import { fileES } from './locale-es.js';

const ATTR = 'data-company-file-init';

/** Tamaño en unidades legibles. 1 kB = 1024 B, como lo cuentan los navegadores. */
function humanSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

class FileInput {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.input = wrapper.querySelector('input[type="file"]');
    if (!this.input) throw new Error('company-theme: campo de archivo sin <input type="file">');

    const data = wrapper.dataset;
    this.accept = data.companyFileAccept || '';
    this.maxMb = Number(data.companyFileMax) || 0;
    this.aWebp = 'companyFileWebp' in data;

    if (this.accept) this.input.setAttribute('accept', this.accept);

    this._build();

    this.input.addEventListener('change', () => {
      this._render();
      this._convertir();
    });
    this._render();

    wrapper.__companyFile = this;
  }

  _build() {
    this.label = this.wrapper.querySelector('.form-label');

    // Las clases se ponen AQUÍ, no se esperan del markup. Antes las ponía el
    // constructor de formularios, así que un campo escrito a mano se quedaba
    // con el input nativo a la vista y salían DOS botones de «Elegir archivo»:
    // el del sistema y el del theme. Quien escribe el HTML solo debería tener
    // que declarar el atributo.
    this.wrapper.classList.add('company-file');
    this.input.classList.add('company-file-native');
    if (this.label) this.label.classList.add('company-file-label');

    this.box = document.createElement('div');
    this.box.className = 'company-file-box';
    this.box.innerHTML = `
      <button type="button" class="company-file-pick">
        <i class="fas fa-paperclip"></i><span></span>
      </button>
      <div class="company-file-chosen" hidden>
        <i class="fas fa-file company-file-icon"></i>
        <span class="company-file-name"></span>
        <span class="company-file-size"></span>
        <button type="button" class="company-file-remove" aria-label="${fileES.remove}">
          <i class="fas fa-xmark"></i>
        </button>
      </div>
      <p class="company-file-error" hidden></p>
    `;

    this.wrapper.appendChild(this.box);

    this.pick = this.box.querySelector('.company-file-pick');
    this.pick.querySelector('span').textContent = fileES.choose;
    this.chosen = this.box.querySelector('.company-file-chosen');
    this.error = this.box.querySelector('.company-file-error');

    // El input nativo no se elimina: es el que viaja en el `FormData`. Solo se
    // esconde, y el botón le pasa el clic.
    this.pick.addEventListener('click', () => this.input.click());

    this.box.querySelector('.company-file-remove').addEventListener('click', () => this.clear());

    this._enableDrop();
  }

  /**
   * Arrastrar y soltar.
   *
   * El borde discontinuo de la caja es el gesto universal de «suéltalo aquí»,
   * así que la gente lo intenta. Sin escuchas, el navegador hace lo suyo: ABRE
   * el archivo y se lleva por delante el formulario a medio llenar. Prometer
   * algo con la forma y no cumplirlo es peor que no prometerlo.
   */
  _enableDrop() {
    const zona = this.box;

    // `dragover` hay que cancelarlo SIEMPRE: si no, el navegador no considera
    // la zona válida y `drop` no llega a dispararse.
    ['dragenter', 'dragover'].forEach((tipo) => {
      zona.addEventListener(tipo, (event) => {
        event.preventDefault();
        zona.classList.add('company-file-over');
      });
    });

    ['dragleave', 'drop'].forEach((tipo) => {
      zona.addEventListener(tipo, (event) => {
        event.preventDefault();
        // `dragleave` salta también al pasar sobre los hijos de la caja: se
        // ignora mientras el puntero siga dentro, o parpadearía.
        if (tipo === 'dragleave' && zona.contains(event.relatedTarget)) return;
        zona.classList.remove('company-file-over');
      });
    });

    zona.addEventListener('drop', (event) => {
      const files = event.dataTransfer && event.dataTransfer.files;
      if (!files || !files.length) return;

      // Se pasan al input nativo, que es el que viaja en el `FormData`.
      // `DataTransfer` es la única forma de asignarle archivos por código.
      const lista = new DataTransfer();
      lista.items.add(files[0]); // un solo archivo: el campo no es múltiple

      this.input.files = lista.files;
      this._render();

      // Solo si se aceptó. Si `_render()` lo rechazó por tamaño, ya vació el
      // campo y dejó el aviso puesto; disparar `change` aquí lo repintaría y
      // borraría el mensaje que el usuario tiene que leer.
      if (!this.file) return;

      // El `change` no salta al asignar `files` por código, y sin él ni el
      // formulario ni la aplicación se enterarían.
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  _render() {
    const file = this.input.files && this.input.files[0];

    this.error.hidden = true;
    this.wrapper.classList.remove('is-invalid');

    if (!file) {
      this.chosen.hidden = true;
      this.pick.hidden = false;
      return;
    }

    if (this.maxMb && file.size > this.maxMb * 1024 * 1024) {
      // Se rechaza aquí para no hacerle subir 40 MB y enterarse al final.
      this.clear();
      this.error.textContent = fileES.tooBig.replace('{max}', this.maxMb);
      this.error.hidden = false;
      this.wrapper.classList.add('is-invalid');
      return;
    }

    this.box.querySelector('.company-file-name').textContent = file.name;
    this.box.querySelector('.company-file-size').textContent = humanSize(file.size);
    this.chosen.hidden = false;
    this.pick.hidden = true;
  }

  /**
   * Cambia la imagen elegida por su versión WebP, sin que nadie tenga que hacer
   * nada.
   *
   * El campo se marca como «preparando» mientras tanto: convertir una foto de
   * móvil lleva un momento, y dar a guardar en ese momento mandaría el original
   * de veinticinco megas, que es justo lo que se está evitando.
   */
  async _convertir() {
    if (!this.aWebp || this.convirtiendo) return;

    const original = this.file;

    if (!original || String(original.type || '').indexOf('image/') !== 0) return;
    if (original.type === 'image/webp') return;

    this.convirtiendo = true;
    this.wrapper.classList.add('company-file-preparando');

    try {
      const listo = await aWebp(original);

      // El mismo fichero de vuelta: no se pudo o no compensaba. Se queda el
      // original y lo convierte el servidor, que es el camino de siempre.
      if (listo === original) return;

      // Reemplazar lo elegido solo se puede con un `DataTransfer`: a un input de
      // archivo no se le asigna un `File` suelto.
      const lista = new DataTransfer();

      lista.items.add(listo);
      this.input.files = lista.files;

      this._render();
    } finally {
      this.convirtiendo = false;
      this.wrapper.classList.remove('company-file-preparando');
    }
  }

  /** Quita el archivo elegido. */
  clear() {
    // `value = ''` es la única forma de vaciar un input de archivo; asignarle
    // otra cosa lo impide el navegador por seguridad.
    this.input.value = '';
    this._render();
  }

  get file() {
    return (this.input.files && this.input.files[0]) || null;
  }

  isValid() {
    if (!this.input.required) return true;
    return !!this.file;
  }

  dispose() {
    this.box.remove();
    delete this.wrapper.__companyFile;
  }
}

/**
 * @param {ParentNode} [root=document]
 */
export function setupFileInputs(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  root.querySelectorAll(`[${ATTR}]`).forEach((wrapper) => {
    if (wrapper.__companyFile) return;
    new FileInput(wrapper);
  });
}

export { FileInput };
export default setupFileInputs;
